import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Button, BodyText } from 'shared-portal-component';

import AppModal from '@/components/common/AppModal/AppModal';
import UpdatePasswordForm from '@/components/organisms/UpdatePassword/UpdatePasswordForm';
import UpdatePasswordProgress from '@/components/molecules/UpdatePasswordProgress/UpdatePasswordProgress';
import { useUpdatePassword } from '@/hooks/auth/useUpdatePassword';
import { closeModal } from '@/store/slices/ui/uiSlice';
import { useAppDispatch } from '@/store';
import type { RootState } from '@/store/rootReducer';
import type { UpdatePasswordPayload } from '@/types/passwordUpdate/PasswordUpdate';

const UpdatePasswordModalContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const isOpen = useSelector((state: RootState) => state.ui.modal === 'updatePassword');
  const { state, handlers } = useUpdatePassword();

  const handleClose = useCallback(() => {
    if (state.isActive) return;
    handlers.reset();
    dispatch(closeModal());
  }, [state.isActive, handlers, dispatch]);

  const handleSubmit = useCallback(
    (values: UpdatePasswordPayload) => {
      void handlers.submit(values);
    },
    [handlers]
  );

  if (!isOpen) return null;

  const showForm = state.stage === 'idle';
  const showProgress = state.isActive;
  const showComplete = state.stage === 'complete';
  const showError = state.stage === 'error';

  return (
    <AppModal
      onClose={handleClose}
      title="Update Password"
      size="md"
    >
      {showForm && (
        <UpdatePasswordForm onSubmit={handleSubmit} />
      )}

      {showProgress && (
        <UpdatePasswordProgress
          stage={state.stage}
          message={state.message}
          processed={state.processed}
          total={state.total}
        />
      )}

      {showComplete && (
        <Container fluid className="py-8">
          <Row spacing="md" justify="center">
            <Col size={12} className="flex flex-col items-center gap-4">
              <BodyText as="p" className="text-center text-lg font-medium">
                Password updated successfully
              </BodyText>
              <Button
                variant="primary"
                size="md"
                onClick={handleClose}
                text="Close"
              />
            </Col>
          </Row>
        </Container>
      )}

      {showError && (
        <Container fluid className="py-8">
          <Row spacing="md" justify="center">
            <Col size={12} className="flex flex-col items-center gap-4">
              <BodyText as="p" className="text-center text-lg font-medium text-destructive">
                {state.message}
              </BodyText>
              {state.errors.length > 0 && (
                <ul className="max-h-40 w-full overflow-y-auto text-sm">
                  {state.errors.map((error, index) => (
                    <li key={index} className="text-destructive mb-1">
                      {error}
                    </li>
                  ))}
                </ul>
              )}
              <Button
                variant="primary"
                size="md"
                onClick={handleClose}
                text="Close"
              />
            </Col>
          </Row>
        </Container>
      )}
    </AppModal>
  );
};

export default UpdatePasswordModalContainer;
